/**
 * skillMap.js — Build 1 · cross-surface skill-id map + one-line recorder.
 * ────────────────────────────────────────────────────────────────
 * Every learning surface funnels its "graded a question" moment through
 * `recordAnswer(...)`, which normalises a raw topic/skill/lesson id into
 * a stable, namespaced skill id and writes it to the canonical ledger.
 *
 * Canonical namespaces (one per surface):
 *   reading:   comprehension categories (inference, vocab_in_context…)
 *   maths:     Maths Academy drills — keyed by activity id
 *   academy:   Quest Academy skills/lessons — keyed by skill/lesson slug
 *   treasure:  Treasure Quest — keyed by world/subject
 *   language:  Language Quest — keyed by activity/skill
 *
 * Keep raw→id normalisation here so a skill practised on two surfaces
 * that share a concept can be reconciled later without touching call
 * sites. For now each surface owns its namespace.
 */
import { recordSkillEvent } from "@/lib/skillLedger";

export const SURFACES = ["reading", "maths", "academy", "treasure", "language"];

/** Normalise any raw id into a stable slug (a-z0-9-, ≤60 chars). */
export function skillSlug(raw) {
  return (
    String(raw ?? "general")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "general"
  );
}

/** Build the canonical namespaced skill id. */
export function skillId(surface, raw) {
  const ns = SURFACES.includes(surface) ? surface : "misc";
  return `${ns}:${skillSlug(raw)}`;
}

/**
 * Record one graded answer into the shared ledger.
 *   surface  — one of SURFACES
 *   raw      — the surface's own skill/topic/lesson/activity id
 *   correct  — boolean
 *   kind     — free label (drill | lesson | mastery | quest | game …)
 *   eventId  — optional; pass a stable id to make the write idempotent
 */
export function recordAnswer({ surface, raw, correct, kind = "practice", skillType = null, eventId = null, meta = null }) {
  if (!surface) return null;
  return recordSkillEvent({
    skillId: skillId(surface, raw),
    correct: !!correct,
    kind,
    skillType: skillType || surface,
    eventId,
    meta,
  });
}
